import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BookOpen, LogOut, Shield, Sun, Moon } from 'lucide-react';
import { useAuth } from '@/features/platform/contexts/auth-context';
import { useTheme } from '@/contexts/theme-context';

interface PortalLayoutProps {
  children: React.ReactNode;
}

export const PortalLayout: React.FC<PortalLayoutProps> = ({ children }) => {
  const { slug } = useParams<{ slug: string }>();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate(`/t/${slug}`);
  };

  return (
    <div className="portal-root">
      {/* Top navigation bar */}
      <nav className="portal-nav">
        <div className="portal-nav-inner">
          {/* Brand */}
          <div className="portal-nav-brand">
            <img src="/brand/light-logo-with-text.svg" alt="Argus" className="h-7 w-auto dark:hidden" />
            <img src="/brand/dark-logo-with-text.svg" alt="Argus" className="h-7 w-auto hidden dark:block" />
            {slug && (
              <span className="portal-nav-tenant">
                <span className="portal-nav-sep">/</span>
                {slug}
              </span>
            )}
          </div>

          {/* User + Logout */}
          <div className="portal-nav-actions">
            <Link
              to="/dev/docs/platform"
              className="p-2 mr-1 rounded-xl border border-slate-250 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors shadow-sm"
              title="Developer docs"
            >
              <BookOpen className="h-4.5 w-4.5" />
            </Link>
            <button
              className="p-2 mr-1 rounded-xl border border-slate-250 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors shadow-sm"
              onClick={toggleTheme}
              title="Toggle theme"
            >
              {theme === "dark" ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
            </button>
            {user && (
              <div className="portal-nav-user">
                <div className="portal-nav-avatar">
                  {(user.name || user.email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="portal-nav-userinfo">
                  <span className="portal-nav-username">{user.name || user.email}</span>
                  <span className="portal-nav-useremail">{user.email}</span>
                </div>
              </div>
            )}
            <button className="portal-nav-logout" onClick={handleLogout} title="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </nav>

      {/* Powered by badge */}
      <div className="portal-powered">
        <Shield className="h-3 w-3" />
        Secure
      </div>

      {/* Main content */}
      <main className="portal-main">
        {children}
      </main>
    </div>
  );
};

export default PortalLayout;
